import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, DatePicker, Select, Input, Alert, Spin, message } from 'antd';
import { DealsApi } from 'api/api';
import { PaymentMethod } from 'api/models';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';
import styled from 'styled-components';
import { Loader } from 'container/styled';

const dealsApi = new DealsApi();

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 24px;
  }
`;

const FormAlert = styled(Alert)`
  margin-bottom: 24px;
`;

interface Props {
  visible: boolean;
  dealId: number;
  paymentId: number;
  onSuccess: () => void;
  onCancel: () => void;
}

const RecordPaymentModal: React.FC<Props> = ({ visible, dealId, paymentId, onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [installment, setInstallment] = useState<any>(null);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<PaymentMethod | undefined>(
    installment?.paymentMethod,
  );

  useEffect(() => {
    if (visible && paymentId) {
      fetchInstallmentDetails();
    }
  }, [visible, paymentId]);

  const fetchInstallmentDetails = async () => {
    try {
      setLoading(true);
      const response = await dealsApi.apiDealsPaymentsIdGet({ id: paymentId });
      setInstallment(response.data);

      // Set initial form values
      form.setFieldsValue({
        amount: response.data.expectedAmount,
        paymentMethod: response.data.paymentMethod,
        transactionReference: response.data.transactionReference,
        notes: response.data.notes,
      });
    } catch (error) {
      // Handle error
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const response = await dealsApi.apiDealsIdPaymentsPaymentIdRecordPost({
        id: dealId,
        paymentId,
        recordDealPaymentCommand: {
          dealId,
          cashFlowId: paymentId,
          ...values,
        },
      });

      if (response.data.isSuccess) {
        message.success(
          response.data.isFullyPaid
            ? t('payments:payment-recorded-deal-completed', 'Payment recorded successfully. Deal is now fully paid!')
            : t('payments:payment-recorded', 'Payment recorded successfully'),
        );

        if (response.data.isFullyPaid) {
          message.info(
            t('payments:remaining-payments-cancelled', '{{count}} remaining payments have been cancelled', {
              count: response.data.canceledCashFlowsCount,
            }),
          );
        }

        onSuccess();
      } else {
        message.error(response.data.errorMessage || t('payments:generic-error', 'Failed to record payment'));
      }
    } catch (error) {
      message.error(t('payments:generic-error', 'Failed to record payment'));
    } finally {
      setLoading(false);
    }
  };

  // Watch for payment method changes from form
  const paymentMethod = Form.useWatch('paymentMethod', form);

  useEffect(() => {
    setCurrentPaymentMethod(paymentMethod);
  }, [paymentMethod]);

  return (
    <StyledModal
      title={t('payments:record-payment', 'Record Payment')}
      visible={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText={t('payments:record', 'Record')}
      confirmLoading={loading}
      width={600}
    >
      {loading ? (
        <Loader>
          <Spin />
        </Loader>
      ) : (
        <>
          {installment && (
            <FormAlert
              message={t('payments:payment-details', 'Payment Details')}
              description={
                <>
                  <div>
                    {t('payments:expected-amount', 'Expected Amount')}: {installment.expectedAmount}
                  </div>
                  <div>
                    {t('payments:due-date', 'Due Date')}: {moment(installment.dueDate).format('LL')}
                  </div>
                </>
              }
              type="info"
              showIcon
            />
          )}

          <Form form={form} layout="vertical">
            <Form.Item
              name="amount"
              label={t('payments:amount', 'Amount')}
              rules={[{ required: true, message: t('payments:amount-required', 'Please enter the payment amount') }]}
            >
              <InputNumber
                className="w-full"
                style={{ width: '100%' }}
                formatter={value => `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/€\s?|(,*)/g, '')}
              />
            </Form.Item>

            <Form.Item
              name="paidDate"
              label={t('payments:payment-date', 'Payment Date')}
              rules={[
                { required: true, message: t('payments:payment-date-required', 'Please select the payment date') },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                placeholder={t('payments:select-payment-date', 'Select Payment Date')}
                format="YYYY-MM-DD"
              />
            </Form.Item>

            <Form.Item
              name="paymentMethod"
              label={t('payments:payment-method', 'Payment Method')}
              rules={[
                { required: true, message: t('payments:payment-method-required', 'Please select payment method') },
              ]}
            >
              <Select>
                <Select.Option value={PaymentMethod.Cash}>
                  <FeatherIcon icon="dollar-sign" size={16} /> {t('payments:cash', 'Cash')}
                </Select.Option>
                <Select.Option value={PaymentMethod.Bank}>
                  <FeatherIcon icon="credit-card" size={16} /> {t('payments:bank-transfer', 'Bank Transfer')}
                </Select.Option>
                <Select.Option value={PaymentMethod.Crypto}>
                  <FeatherIcon icon="shield" size={16} /> {t('payments:crypto', 'Crypto')}
                </Select.Option>
                <Select.Option value={PaymentMethod.Other}>
                  <FeatherIcon icon="help-circle" size={16} /> {t('payments:other', 'Other')}
                </Select.Option>
              </Select>
            </Form.Item>

            {(currentPaymentMethod === PaymentMethod.Bank || currentPaymentMethod === PaymentMethod.Crypto) && (
              <Form.Item
                name="transactionReference"
                label={t('payments:transaction-reference', 'Transaction Reference')}
                rules={[
                  {
                    required: true,
                    message: t('payments:transaction-reference-required', 'Please enter transaction reference'),
                  },
                ]}
              >
                <Input
                  placeholder={t('payments:transaction-reference-placeholder', 'e.g., Transaction ID, Check Number')}
                />
              </Form.Item>
            )}

            <Form.Item name="notes" label={t('payments:notes', 'Notes')}>
              <Input.TextArea
                rows={4}
                placeholder={t('payments:notes-placeholder', 'Add any additional notes about this payment')}
              />
            </Form.Item>
          </Form>
        </>
      )}
    </StyledModal>
  );
};

export default RecordPaymentModal;
